import React, { useState, useEffect } from 'react';
import { X, BarChart3, TrendingUp, Clock, Users, Calendar, Download, Filter } from 'lucide-react';
import { api } from '../auth/authStore';
import toast from 'react-hot-toast';

interface TaskAnalyticsProps {
  isOpen: boolean;
  onClose: () => void;
}

interface AnalyticsData {
  overview: {
    totalTasks: number;
    completedTasks: number;
    averageCompletionTime: number;
    onTimeCompletionRate: number;
    totalHoursLogged: number;
    billableHours: number;
  };
  tasksByStatus: Array<{
    status: string;
    count: number;
    percentage: number;
  }>;
  tasksByType: Array<{
    type: string;
    count: number;
    averageHours: number;
    completionRate: number;
  }>;
  tasksByPriority: Array<{
    priority: string;
    count: number;
    averageCompletionTime: number;
  }>;
  productivityMetrics: {
    tasksCompletedThisWeek: number;
    tasksCompletedLastWeek: number;
    averageTasksPerDay: number;
    peakProductivityDay: string;
    utilizationRate: number;
  };
  teamPerformance: Array<{
    userId: string;
    userName: string;
    tasksCompleted: number;
    hoursLogged: number;
    averageCompletionTime: number;
    onTimeRate: number;
  }>;
  timeAnalysis: {
    totalTimeByMonth: Array<{
      month: string;
      hours: number;
      billableHours: number;
    }>;
    averageTimeByTaskType: Array<{
      type: string;
      averageHours: number;
      estimatedHours: number;
      variance: number;
    }>;
  };
}

export const TaskAnalytics: React.FC<TaskAnalyticsProps> = ({
  isOpen,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });
  const [selectedView, setSelectedView] = useState<'overview' | 'productivity' | 'team' | 'time'>('overview');

  useEffect(() => {
    if (isOpen) {
      fetchAnalytics();
    }
  }, [isOpen, dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await api.get('/firm-admin/tasks/analytics', {
        params: {
          from: dateRange.from,
          to: dateRange.to
        }
      });
      
      if (response.data.success) {
        setAnalytics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async () => {
    try {
      const response = await api.get('/firm-admin/tasks/analytics/export', {
        params: {
          from: dateRange.from,
          to: dateRange.to,
          format: 'pdf'
        },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `task-analytics-${dateRange.from}-to-${dateRange.to}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  const formatHours = (hours: number) => {
    return `${hours.toFixed(1)}h`;
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Task Analytics & Reports</h2>
            <p className="text-sm text-gray-600 mt-1">
              Insights and metrics for task management performance
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                className="px-3 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                className="px-3 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={exportReport}
              className="flex items-center px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : analytics ? (
          <div className="p-6">
            {/* Navigation Tabs */}
            <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
              {[
                { key: 'overview', label: 'Overview', icon: BarChart3 },
                { key: 'productivity', label: 'Productivity', icon: TrendingUp },
                { key: 'team', label: 'Team Performance', icon: Users },
                { key: 'time', label: 'Time Analysis', icon: Clock }
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setSelectedView(key as any)}
                  className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    selectedView === key
                      ? 'bg-white text-primary-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {label}
                </button>
              ))}
            </div>

            {/* Overview Tab */}
            {selectedView === 'overview' && (
              <div className="space-y-6">
                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="bg-blue-50 p-6 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-600 font-medium">Total Tasks</p>
                        <p className="text-3xl font-bold text-blue-900">{analytics.overview.totalTasks}</p>
                        <p className="text-sm text-blue-600">
                          {analytics.overview.completedTasks} completed ({formatPercentage((analytics.overview.completedTasks / analytics.overview.totalTasks) * 100)})
                        </p>
                      </div>
                      <BarChart3 className="w-8 h-8 text-blue-600" />
                    </div>
                  </div>

                  <div className="bg-green-50 p-6 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-600 font-medium">On-Time Rate</p>
                        <p className="text-3xl font-bold text-green-900">{formatPercentage(analytics.overview.onTimeCompletionRate)}</p>
                        <p className="text-sm text-green-600">
                          Avg completion: {analytics.overview.averageCompletionTime} days
                        </p>
                      </div>
                      <TrendingUp className="w-8 h-8 text-green-600" />
                    </div>
                  </div>

                  <div className="bg-purple-50 p-6 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-600 font-medium">Hours Logged</p>
                        <p className="text-3xl font-bold text-purple-900">{formatHours(analytics.overview.totalHoursLogged)}</p>
                        <p className="text-sm text-purple-600">
                          {formatHours(analytics.overview.billableHours)} billable ({formatPercentage((analytics.overview.billableHours / analytics.overview.totalHoursLogged) * 100)})
                        </p>
                      </div>
                      <Clock className="w-8 h-8 text-purple-600" />
                    </div>
                  </div>
                </div>

                {/* Tasks by Status */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Tasks by Status</h3>
                  <div className="space-y-3">
                    {analytics.tasksByStatus.map((item) => (
                      <div key={item.status} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-4 h-4 bg-primary-600 rounded mr-3"></div>
                          <span className="text-sm font-medium text-gray-900">
                            {item.status.replace('_', ' ')}
                          </span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-gray-600">{item.count} tasks</span>
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-primary-600 h-2 rounded-full"
                              style={{ width: `${item.percentage}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-gray-900 w-12 text-right">
                            {formatPercentage(item.percentage)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tasks by Type */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Tasks by Type</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 text-sm font-medium text-gray-600">Type</th>
                          <th className="text-right py-2 text-sm font-medium text-gray-600">Count</th>
                          <th className="text-right py-2 text-sm font-medium text-gray-600">Avg Hours</th>
                          <th className="text-right py-2 text-sm font-medium text-gray-600">Completion Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.tasksByType.map((item) => (
                          <tr key={item.type} className="border-b border-gray-100">
                            <td className="py-3 text-sm text-gray-900">{item.type.replace('_', ' ')}</td>
                            <td className="py-3 text-sm text-gray-600 text-right">{item.count}</td>
                            <td className="py-3 text-sm text-gray-600 text-right">{formatHours(item.averageHours)}</td>
                            <td className="py-3 text-sm text-gray-600 text-right">{formatPercentage(item.completionRate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Productivity Tab */}
            {selectedView === 'productivity' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">{analytics.productivityMetrics.tasksCompletedThisWeek}</p>
                      <p className="text-sm text-gray-600">Tasks This Week</p>
                      <p className="text-xs text-green-600 mt-1">
                        +{analytics.productivityMetrics.tasksCompletedThisWeek - analytics.productivityMetrics.tasksCompletedLastWeek} from last week
                      </p>
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">{analytics.productivityMetrics.averageTasksPerDay}</p>
                      <p className="text-sm text-gray-600">Avg Tasks/Day</p>
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">{analytics.productivityMetrics.peakProductivityDay}</p>
                      <p className="text-sm text-gray-600">Peak Day</p>
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">{formatPercentage(analytics.productivityMetrics.utilizationRate)}</p>
                      <p className="text-sm text-gray-600">Utilization Rate</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Team Performance Tab */}
            {selectedView === 'team' && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Team Performance</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 text-sm font-medium text-gray-600">Team Member</th>
                        <th className="text-right py-2 text-sm font-medium text-gray-600">Tasks Completed</th>
                        <th className="text-right py-2 text-sm font-medium text-gray-600">Hours Logged</th>
                        <th className="text-right py-2 text-sm font-medium text-gray-600">Avg Completion Time</th>
                        <th className="text-right py-2 text-sm font-medium text-gray-600">On-Time Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.teamPerformance.map((member) => (
                        <tr key={member.userId} className="border-b border-gray-100">
                          <td className="py-3 text-sm font-medium text-gray-900">{member.userName}</td>
                          <td className="py-3 text-sm text-gray-600 text-right">{member.tasksCompleted}</td>
                          <td className="py-3 text-sm text-gray-600 text-right">{formatHours(member.hoursLogged)}</td>
                          <td className="py-3 text-sm text-gray-600 text-right">{member.averageCompletionTime} days</td>
                          <td className="py-3 text-sm text-gray-600 text-right">{formatPercentage(member.onTimeRate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Time Analysis Tab */}
            {selectedView === 'time' && (
              <div className="space-y-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Time Variance by Task Type</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 text-sm font-medium text-gray-600">Task Type</th>
                          <th className="text-right py-2 text-sm font-medium text-gray-600">Actual Avg</th>
                          <th className="text-right py-2 text-sm font-medium text-gray-600">Estimated Avg</th>
                          <th className="text-right py-2 text-sm font-medium text-gray-600">Variance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.timeAnalysis.averageTimeByTaskType.map((item) => (
                          <tr key={item.type} className="border-b border-gray-100">
                            <td className="py-3 text-sm text-gray-900">{item.type.replace('_', ' ')}</td>
                            <td className="py-3 text-sm text-gray-600 text-right">{formatHours(item.averageHours)}</td>
                            <td className="py-3 text-sm text-gray-600 text-right">{formatHours(item.estimatedHours)}</td>
                            <td className={`py-3 text-sm text-right font-medium ${
                              item.variance > 0 ? 'text-red-600' : 'text-green-600'
                            }`}>
                              {item.variance > 0 ? '+' : ''}{formatPercentage(item.variance)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-12 text-center text-gray-500">
            No analytics data available
          </div>
        )}
      </div>
    </div>
  );
};
